/**
 * Frame scheduler for motion that cannot run as a WAAPI effect: scroll
 * offsets, measured size caps and continuous pointer-driven motion.
 *
 * Modelled on the CodeCanvas motion controller: one scheduler owns the only
 * requestAnimationFrame loop, every motion runs on an exclusive channel, and
 * starting a channel again replaces its motion instead of stacking loops.
 * Renderers receive values; they never own frame loops. The loop is released
 * as soon as the last channel settles.
 *
 * Timing and curves come from `STREAMLAIN_MOTION`; this module only drives them.
 */
import {
  STREAMLAIN_MOTION,
  type StreamLainMotionKind,
  streamLainMotionProgress,
} from './streamlain-motion-curves';
import {
  isStreamLainMotionEnabled,
  type StreamLainMotionDomain,
} from './streamlain-motion-policy';

export type StreamLainMotionChannel = symbol;

export interface StreamLainMotionClock {
  cancelFrame(request: number): void;
  /** The current time on the time base of the frame timestamps. */
  now(): number;
  prefersReducedMotion(): boolean;
  requestFrame(callback: (time: number) => void): number;
}

interface ActiveTween {
  readonly kind: 'tween';
  readonly domain: StreamLainMotionDomain;
  readonly durationMs: number;
  readonly easing: (progress: number) => number;
  readonly from: number;
  readonly to: number;
  readonly onUpdate: (value: number) => void;
  readonly onComplete?: () => void;
  readonly onCancel?: () => void;
  readonly requestedAt: number;
  startedAt: number | null;
  value: number;
}

interface ActiveLoop {
  readonly kind: 'loop';
  readonly onFrame: (deltaMs: number) => boolean;
  readonly onCancel?: () => void;
  readonly requestedAt: number;
  lastFrameAt: number | null;
}

type ActiveMotion = ActiveTween | ActiveLoop;

export function streamLainMotionChannel(
  description: string
): StreamLainMotionChannel {
  return Symbol(description);
}

export class StreamLainMotionScheduler {
  private readonly motions = new Map<StreamLainMotionChannel, ActiveMotion>();
  private frameRequest: number | undefined;

  constructor(private readonly clock: StreamLainMotionClock) {}

  prefersReducedMotion(): boolean {
    return this.clock.prefersReducedMotion() || !isStreamLainMotionEnabled();
  }

  isActive(channel: StreamLainMotionChannel): boolean {
    return this.motions.has(channel);
  }

  /** Current value of a running tween, so an interruption continues from where the motion is. */
  value(channel: StreamLainMotionChannel): number | undefined {
    const motion = this.motions.get(channel);
    return motion?.kind === 'tween' ? motion.value : undefined;
  }

  /**
   * Tween a number along a shared motion kind. Settles synchronously when
   * there is nothing to move or reduced motion is preferred.
   */
  animate(options: {
    readonly channel: StreamLainMotionChannel;
    readonly kind: StreamLainMotionKind;
    readonly domain?: StreamLainMotionDomain;
    readonly from: number;
    readonly to: number;
    readonly onUpdate: (value: number) => void;
    readonly onComplete?: () => void;
    readonly onCancel?: () => void;
  }): void {
    this.cancel(options.channel);
    if (
      Math.abs(options.to - options.from) <= Number.EPSILON ||
      this.clock.prefersReducedMotion() ||
      !isStreamLainMotionEnabled(options.domain)
    ) {
      options.onUpdate(options.to);
      options.onComplete?.();
      return;
    }
    this.motions.set(options.channel, {
      kind: 'tween',
      domain: options.domain ?? 'app',
      durationMs: STREAMLAIN_MOTION[options.kind].duration,
      easing: streamLainMotionProgress(options.kind),
      from: options.from,
      to: options.to,
      onUpdate: options.onUpdate,
      onComplete: options.onComplete,
      onCancel: options.onCancel,
      requestedAt: this.clock.now(),
      startedAt: null,
      value: options.from,
    });
    this.scheduleFrame();
  }

  /**
   * Run per-frame work (for example pointer-driven edge scrolling) until
   * `onFrame` returns false or the channel is cancelled. `deltaMs` is the
   * time since the previous frame of this channel, 0 on its first frame.
   */
  loop(
    channel: StreamLainMotionChannel,
    onFrame: (deltaMs: number) => boolean,
    onCancel?: () => void
  ): void {
    this.cancel(channel);
    this.motions.set(channel, {
      kind: 'loop',
      onFrame,
      onCancel,
      requestedAt: this.clock.now(),
      lastFrameAt: null,
    });
    this.scheduleFrame();
  }

  cancel(channel: StreamLainMotionChannel): void {
    const motion = this.motions.get(channel);
    if (!motion) return;
    this.motions.delete(channel);
    this.releaseFrameIfIdle();
    motion.onCancel?.();
  }

  dispose(): void {
    for (const channel of [...this.motions.keys()]) this.cancel(channel);
  }

  private scheduleFrame(): void {
    if (this.frameRequest !== undefined) return;
    this.frameRequest = this.clock.requestFrame(time => this.tick(time));
  }

  private releaseFrameIfIdle(): void {
    if (this.motions.size > 0 || this.frameRequest === undefined) return;
    this.clock.cancelFrame(this.frameRequest);
    this.frameRequest = undefined;
  }

  private tick(time: number): void {
    this.frameRequest = undefined;
    const reduced = this.clock.prefersReducedMotion();
    for (const [channel, motion] of [...this.motions]) {
      if (this.motions.get(channel) !== motion) continue;
      // A frame that waited behind a busy main thread is stamped with the time
      // it was due, which can lie before the motion was asked for. Counting
      // from that stamp would spend the motion's time before its first step.
      const frameAt = Math.max(time, motion.requestedAt);
      if (motion.kind === 'loop') {
        const delta =
          motion.lastFrameAt === null ? 0 : frameAt - motion.lastFrameAt;
        motion.lastFrameAt = frameAt;
        if (!motion.onFrame(delta) && this.motions.get(channel) === motion) {
          this.motions.delete(channel);
        }
        continue;
      }
      motion.startedAt ??= frameAt;
      const linear =
        reduced || !isStreamLainMotionEnabled(motion.domain)
          ? 1
          : Math.min(
              1,
              Math.max(0, (frameAt - motion.startedAt) / motion.durationMs)
            );
      motion.value =
        linear >= 1
          ? motion.to
          : motion.from + (motion.to - motion.from) * motion.easing(linear);
      motion.onUpdate(motion.value);
      if (linear < 1 || this.motions.get(channel) !== motion) continue;
      this.motions.delete(channel);
      motion.onComplete?.();
    }
    if (this.motions.size > 0) this.scheduleFrame();
  }
}

/** Clock bound to the browser's global frame loop and reduced-motion preference. */
export function browserStreamLainMotionClock(): StreamLainMotionClock {
  return {
    cancelFrame: request => globalThis.cancelAnimationFrame(request),
    now: () => globalThis.performance.now(),
    prefersReducedMotion: () =>
      globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ??
      false,
    requestFrame: callback => globalThis.requestAnimationFrame(callback),
  };
}

export const streamLainMotionScheduler = new StreamLainMotionScheduler(
  browserStreamLainMotionClock()
);
